import { FC, useState } from 'react';
import CommonButton from 'components/CommonButton/CommonButton';
import DefaultModal from 'components/Modals/DefaultModal';
import { getAppointmentEndTime } from 'components/TableColumn/utils/getAppointmentTime';
import { datetimeFormat } from 'utils/datetime';
import getPractitionerName from 'utils/getPractitionerName';
import { IAppointmentDetail } from 'interfaces/appointments';
import { useForm } from 'react-hook-form';
import axiosInstance from 'apis/axiosInstance';
import useMatchMutate from 'hooks/useMatchMutate';
import { renderToast } from 'components/Toast/Toast';
import useModal from 'hooks/useModal';
import moment from 'moment';
import { FIRSTIN_SYSTEM } from 'utils/constants';

interface KeepBothAppointmentsModalProps {
  appointment: IAppointmentDetail;
  awaitingAppointments: any[];
  onClose: () => void;
}

const getStartTimeForDisplay = (appointment: IAppointmentDetail): string => {
  let startTime = appointment.startTime || '';

  if (appointment.linkedAppointment?.startTime) {
    const childTime = moment(appointment.startTime, 'HH:mm:ss');
    const linkedTime = moment(
      appointment.linkedAppointment.startTime,
      'HH:mm:ss'
    );
    startTime = childTime.isBefore(linkedTime)
      ? appointment.startTime
      : appointment.linkedAppointment.startTime;
  }

  return startTime ? moment(startTime, 'HH:mm:ss').format('hh:mm A') : '';
};

const KeepBothAppointmentsModal: FC<KeepBothAppointmentsModalProps> = ({
  appointment,
  awaitingAppointments,
  onClose,
}) => {
  const matchMutate = useMatchMutate();
  const { setIsAllowed } = useModal({ onClose });
  const [selectedApptIds, setSelectedApptIds] = useState<Set<string>>(
    new Set()
  );

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm();

  const toggleApptSelection = (apptId: string) => {
    setSelectedApptIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(apptId)) {
        newSet.delete(apptId);
      } else {
        newSet.add(apptId);
      }

      return newSet;
    });
  };

  const onSubmit = async () => {
    setIsAllowed(false);

    const payload = [
      {
        appointmentId: appointment.id,
        status: 'accepted',
        isAcceptedOverride: true,
      },
    ];

    selectedApptIds.forEach((apptId) => {
      payload.push({
        appointmentId: apptId,
        status: 'accepted',
        isAcceptedOverride: true,
      });
    });

    try {
      await axiosInstance.patch('/appointments', payload);
      await matchMutate(/\/appointments\?[\s\S]+/);
      renderToast({
        type: 'success',
        message: `Successfully accepted appointments`,
      });
      onClose();
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    } finally {
      setIsAllowed(true);
    }
  };

  return (
    <DefaultModal>
      <div className="max-w-[48rem] min-h-[32rem] rounded-[1rem] border border-light-grey bg-white p-[2rem]">
        <div className="text-14 leading-[2.1rem] flex flex-col gap-y-1">
          <section className="flex flex-col">
            <h3 className="text-20 font-bold leading-[3rem]">
              Keep Appointments?
            </h3>
            <div className="mt-1.6 mb-2.4">
              <div>
                Appointment Date:{' '}
                <span className="font-bold">
                  {datetimeFormat({
                    dateString: appointment.appointmentDate,
                    format: 'YYYY-MM-DD',
                    pattern: 'ddd, MMM D, YYYY',
                  })}
                </span>
              </div>
              <div>
                Time:{' '}
                <span className="font-bold">
                  {getStartTimeForDisplay(appointment)}
                  {' - '}
                  {getAppointmentEndTime(appointment)}
                </span>
              </div>
              <div>
                Doctor:{' '}
                <span className="font-bold">
                  {getPractitionerName(appointment.doctor)}
                </span>
              </div>
            </div>
          </section>
          <section className="flex flex-col">
            <h4 className="text-14 font-bold mb-1.6">
              Also keep existing appointments below:
            </h4>
            <div className="flex flex-col gap-y-1.2 max-h-[50rem] overflow-y-auto scrollbar">
              {awaitingAppointments.map((awaitingAppt) => {
                const appointmentDate = awaitingAppt.appointmentDate
                  ? moment(awaitingAppt.appointmentDate, 'YYYY-MM-DD').format(
                      'MMM DD, YYYY'
                    )
                  : '';

                let startTime = awaitingAppt.startTime || '';
                if (awaitingAppt.linkedAppointment?.startTime) {
                  const childTime = moment(awaitingAppt.startTime, 'HH:mm:ss');
                  const linkedTime = moment(
                    awaitingAppt.linkedAppointment.startTime,
                    'HH:mm:ss'
                  );
                  startTime = childTime.isBefore(linkedTime)
                    ? awaitingAppt.startTime
                    : awaitingAppt.linkedAppointment.startTime;
                }
                const formattedStartTime = startTime
                  ? moment(startTime, 'HH:mm:ss').format('hh:mm A')
                  : '';

                const doctorName =
                  awaitingAppt.doctor?.name ||
                  awaitingAppt.practitioner?.name ||
                  '';

                const isFirstin = awaitingAppt.pmsType === FIRSTIN_SYSTEM;
                const isSelected = selectedApptIds.has(awaitingAppt.id);

                return (
                  <div
                    key={awaitingAppt.id}
                    className="rounded-[0.8rem] bg-[#FFF9EE] border border-[#F1A80933] p-1.2 text-12 text-darkest-grey flex items-start gap-x-1.2"
                  >
                    {isFirstin && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleApptSelection(awaitingAppt.id)}
                        className="w-2.2 h-2.2 accent-magenta cursor-pointer mt-0.4 flex-shrink-0"
                      />
                    )}
                    <div className="flex-1">
                      <div>
                        Appointment Date:{' '}
                        <span className="font-bold">{appointmentDate}</span>
                      </div>
                      <div>
                        Time:{' '}
                        <span className="font-bold">
                          {formattedStartTime}
                          {awaitingAppt.endTime
                            ? ` - ${moment(awaitingAppt.endTime, 'HH:mm:ss').format('hh:mm A')}`
                            : ''}
                        </span>
                      </div>
                      <div>
                        Doctor: <span className="font-bold">{doctorName}</span>
                      </div>
                      <div>
                        Booked from:{' '}
                        <span className="font-bold">
                          {isFirstin ? 'FirstIn' : 'PMS'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              <p className="mt-2">
                Once this is completed, all selected appointments will be
                removed from the Dashboard but will continue to appear in the
                Accepted tab.
              </p>
            </div>
          </section>
          <section className="flex gap-x-1.6 justify-end mt-2.4">
            <CommonButton
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </CommonButton>
            <CommonButton
              onClick={handleSubmit(onSubmit)}
              isLoading={isSubmitting}
            >
              Yes, accept
            </CommonButton>
          </section>
        </div>
      </div>
    </DefaultModal>
  );
};

export default KeepBothAppointmentsModal;
